import { CopyCheck } from 'lucide-react';
import { useState } from 'react';
import { ComposerDecision } from '../composer-gate-context';
import { DecisionFooter } from './decision-footer';

export interface ExistingCandidate {
  id: string;
  name: string;
  createdAt?: string;
  matchReasons?: string[];
}

export interface ExistingCandidatesCardProps {
  id?: string;
  /** What kind of resource nearly duplicated. */
  kind: 'campaign' | 'lead_form';
  proposedName?: string;
  candidates: ExistingCandidate[];
  onPrompt?: (text: string) => void;
}

const KIND_LABEL: Record<ExistingCandidatesCardProps['kind'], string> = {
  campaign: 'campaign',
  lead_form: 'lead form',
};

function formatWhen(iso?: string): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
  }).format(date);
}

/**
 * Duplicate-protection picker (Phase 4 #79 #151). Shown when the create tool
 * found a recent, similar {campaign,lead form} instead of creating one. The
 * owner either reuses an existing one or tells Claire to create a new one
 * anyway — no silent duplicate on the same budget.
 */
export function ExistingCandidatesCard({
  id,
  kind,
  proposedName,
  candidates,
  onPrompt,
}: ExistingCandidatesCardProps) {
  const kindLabel = KIND_LABEL[kind];
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);
  const selected = candidates.find((candidate) => candidate.id === selectedId);
  const respond = (text: string, label: string) => {
    if (outcome || !onPrompt) return;
    setOutcome(label);
    onPrompt(text);
  };
  const reuse = () => {
    if (selected)
      respond(
        `Use the existing ${kindLabel} "${selected.name}" (${selected.id}).`,
        'Using selected item.'
      );
  };
  const createNew = () =>
    respond(
      `Create a new ${kindLabel} anyway${proposedName ? ` called "${proposedName}"` : ''}.`,
      'New item requested.'
    );
  const cancel = () =>
    respond(`Cancel creating this ${kindLabel}.`, 'Cancelled.');
  const composerGate =
    onPrompt && !outcome
      ? {
          sentence: `Use an existing ${kindLabel}, or create a new one?`,
          onAccept: reuse,
          acceptLabel: 'Use selected',
          acceptDisabled: !selected,
          acceptDisabledReason: `Choose an existing ${kindLabel} first.`,
          onReject: cancel,
          rejectLabel: 'Cancel',
          onEdit: createNew,
          editLabel: 'Create new',
        }
      : null;

  if (outcome) return null;

  return (
    <ComposerDecision
      id={id ?? `existing-candidates:${kind}`}
      gate={composerGate}
    >
      <div
        className="w-full rounded-lg border bg-card p-4 sm:max-w-sm"
        data-testid="claire-card-existing-candidates"
      >
        <div className="flex items-center gap-2 text-sm font-medium">
          <CopyCheck className="size-4 text-amber-600 dark:text-amber-500" />
          You may already have this {kindLabel}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Choose an existing {kindLabel}, or create a new one.
        </p>

        <ul className="mt-3 space-y-2">
          {candidates.map((candidate) => {
            const when = formatWhen(candidate.createdAt);
            return (
              <li
                key={candidate.id}
                className="rounded-md border bg-background p-2 text-xs"
              >
                <label className="flex items-center gap-2 font-medium">
                  <input
                    type="radio"
                    name={`candidate-${id ?? kind}`}
                    value={candidate.id}
                    checked={selectedId === candidate.id}
                    disabled={!!outcome || !onPrompt}
                    onChange={() => setSelectedId(candidate.id)}
                  />
                  {candidate.name}
                </label>
                <div className="mt-0.5 text-muted-foreground">
                  {[
                    when ? `created ${when}` : null,
                    candidate.matchReasons?.length
                      ? candidate.matchReasons.join(', ')
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              </li>
            );
          })}
        </ul>

        {onPrompt ? (
          <div className="mt-3">
            <DecisionFooter
              hideSentence
              sentence={`Use an existing ${kindLabel}, or create a new one?`}
              onAccept={reuse}
              acceptLabel="Use selected"
              acceptDisabled={!selected}
              acceptDisabledReason={`Choose an existing ${kindLabel} first.`}
              onReject={cancel}
              rejectLabel="Cancel"
              onEdit={createNew}
              editLabel="Create new"
            />
          </div>
        ) : null}
      </div>
    </ComposerDecision>
  );
}
